import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { ArrowRight, ArrowLeft, Loader2, Check, Circle, Clock } from 'lucide-react';
import { apiPost, apiPatch } from '@/lib/api';
import { streamAICall } from '@/lib/sse';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { createCampaign } from '@/lib/campaigns';
import type { CompanyResearchResponse, Lead, EmailGenerateResponse } from '@hypema/shared';

type Phase = 'input' | 'manual' | 'processing';

interface ChecklistItem {
  label: string;
  status: 'pending' | 'active' | 'done' | 'error';
  detail?: string;
}

const LEAD_COUNT = 5;

const API_URL = (import.meta.env.VITE_API_URL || '') + '/api';

function normalizeUrl(input: string): string {
  const trimmed = input.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

/**
 * Wraps streamAICall in a promise so it can be awaited.
 * Returns the result data. Updates status via onStatus callback.
 */
function streamAICallAsync<T>(
  url: string,
  body: unknown,
  onStatus: (msg: string) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    streamAICall<T>({
      url,
      body,
      onStatus: (event) => {
        if (event.type === 'searching' || event.type === 'analyzing' || event.type === 'writing') {
          onStatus(event.message);
        }
      },
      onResult: (data) => resolve(data),
      onError: (err) => reject(new Error(err)),
    });
  });
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { refreshUser } = useAuth();
  const [phase, setPhase] = useState<Phase>('input');
  const [companyUrl, setCompanyUrl] = useState('');
  const [checklist, setChecklist] = useState<ChecklistItem[]>([
    { label: 'Researching your company', status: 'pending', detail: '~45 sec' },
    { label: 'Setting up your profile', status: 'pending' },
    { label: 'Finding target leads', status: 'pending', detail: '~50 sec' },
    { label: 'Writing personalized emails', status: 'pending', detail: `~${LEAD_COUNT * 10} sec` },
    { label: 'Preparing your campaign', status: 'pending' },
  ]);
  const [progress, setProgress] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const abortRef = useRef(false);

  // Manual form fields (no website)
  const [manualCompany, setManualCompany] = useState('');
  const [manualProduct, setManualProduct] = useState('');
  const [manualIndustry, setManualIndustry] = useState('');
  const [manualAudience, setManualAudience] = useState('');
  const [manualName, setManualName] = useState('');
  const [manualRole, setManualRole] = useState('');
  const [manualRegion, setManualRegion] = useState('');

  // Timer for processing phase
  useEffect(() => {
    if (phase !== 'processing') { setElapsed(0); return; }
    const interval = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [phase]);

  function updateItem(index: number, patch: Partial<ChecklistItem>) {
    setChecklist((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  }

  async function handleGo() {
    if (!companyUrl.trim()) return;
    setPhase('processing');
    abortRef.current = false;

    const url = normalizeUrl(companyUrl);

    try {
      // ── Step 1: Research company (with SSE streaming) ──
      updateItem(0, { status: 'active' });
      setProgress(5);

      const research = await streamAICallAsync<CompanyResearchResponse>(
        `${API_URL}/ai/research-company`,
        { url },
        (msg) => updateItem(0, { detail: msg }),
      );

      updateItem(0, { status: 'done', detail: research.companyName || 'Done' });
      setProgress(25);

      if (abortRef.current) return;

      // ── Step 2: Save tenant profile ──
      updateItem(1, { status: 'active' });

      const senderName = research.ceoName || '';
      const senderRole = research.ceoRole || 'CEO';

      try {
        await apiPost('/tenants', {
          slug: 'dev-tenant',
          displayName: research.companyName || 'My Company',
          identityBlock: research.companyName || 'My Company',
          postalAddress: '-',
          primaryContactEmail: 'onboarding@example.com',
          website: url,
        });
      } catch {
        // Tenant already exists — that's fine
      }

      try {
        await apiPatch('/tenants/current', {
          displayName: research.companyName || undefined,
          companyDescription: research.description || undefined,
          industry: research.industry || undefined,
          productName: research.productName || undefined,
          valueProposition: research.valueProposition || undefined,
          targetAudience: research.targetAudience || undefined,
          senderFullName: senderName || undefined,
          senderRole: senderRole || undefined,
          website: url,
        });
      } catch {
        // Non-critical — continue
      }

      updateItem(1, { status: 'done' });
      setProgress(35);

      if (abortRef.current) return;

      // ── Step 3: Generate leads (with SSE streaming) ──
      updateItem(2, { status: 'active' });

      const leadsResult = await streamAICallAsync<{ leads: any[]; count: number }>(
        `${API_URL}/ai/generate-leads`,
        {
          industry: research.industry || 'Technology',
          roleTitle: research.targetAudience || 'Decision Makers',
          region: 'Global',
          count: LEAD_COUNT,
          companyName: research.companyName,
          companyDescription: research.description,
          productName: research.productName,
        },
        (msg) => updateItem(2, { detail: msg }),
      );

      updateItem(2, { status: 'done', detail: `${leadsResult.count} leads found` });
      setProgress(55);

      if (abortRef.current) return;

      // ── Step 4: Generate emails ──
      updateItem(3, { status: 'active' });

      // Build a realistic topicGoalText from research data
      const topicGoalText = [
        research.productName && `Introducing ${research.productName}`,
        research.targetAudience && `to ${research.targetAudience}`,
        research.valueProposition && `— ${research.valueProposition}`,
      ].filter(Boolean).join(' ') || `Introducing ${research.companyName || 'our product'} to potential customers`;

      // Create campaign
      await createCampaign({
        name: 'First Campaign',
        product: research.productName || research.companyName || '',
        tone: 'professional',
        context: topicGoalText,
      });

      // Use the leads we just generated (they have IDs from the DB)
      const leads = leadsResult.leads;

      if (leads.length === 0) {
        updateItem(3, { status: 'error', detail: 'No leads found to write emails for' });
      } else {
        let emailCount = 0;
        let consecutiveErrors = 0;
        for (const lead of leads) {
          if (abortRef.current) return;
          try {
            updateItem(3, { detail: `Writing email ${emailCount + 1} of ${leads.length} — ${lead.firstName || ''} ${lead.lastName || ''} @ ${lead.company || ''}` });
            await apiPost<EmailGenerateResponse>('/emails/generate', {
              leadId: lead.id,
              topicGoalText,
            });
            emailCount++;
            consecutiveErrors = 0;
            setProgress(55 + Math.round((emailCount / leads.length) * 35));
          } catch (err: any) {
            consecutiveErrors++;
            console.warn(`Email generation failed for ${lead.email}:`, err.message);
            if (consecutiveErrors >= 3) {
              const reason = err.message?.includes('token') || err.message?.includes('TOKEN')
                ? 'Token limit reached'
                : 'Generation failed';
              updateItem(3, { detail: `${emailCount} of ${leads.length} written (${reason})` });
              break;
            }
          }
        }

        if (emailCount > 0) {
          updateItem(3, { status: 'done', detail: `${emailCount} emails written` });
        } else {
          updateItem(3, { status: 'error', detail: 'Could not generate emails — you may be out of tokens' });
        }
      }
      setProgress(95);

      // ── Step 5: Finalize ──
      updateItem(4, { status: 'active' });
      updateItem(4, { status: 'done' });
      setProgress(100);

      // Brief pause to show completion, then redirect
      await new Promise((r) => setTimeout(r, 800));
      navigate('/onboarding/ready');
    } catch (err: any) {
      toast.error(err.message || 'Something went wrong. Please try again.');
      setPhase('input');
      setChecklist((prev) => prev.map((item) => ({ ...item, status: 'pending' as const, detail: undefined })));
      setProgress(0);
    }
  }

  async function handleGoManual() {
    if (!manualCompany.trim()) return;

    setChecklist([
      { label: 'Setting up your profile', status: 'pending' },
      { label: 'Finding target leads', status: 'pending', detail: '~50 sec' },
      { label: 'Writing personalized emails', status: 'pending', detail: `~${LEAD_COUNT * 10} sec` },
      { label: 'Preparing your campaign', status: 'pending' },
    ]);
    setPhase('processing');
    abortRef.current = false;

    const updateManualItem = (index: number, patch: Partial<ChecklistItem>) => {
      setChecklist((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
    };

    try {
      // ── Step 1: Save profile ──
      updateManualItem(0, { status: 'active' });
      setProgress(10);

      try {
        await apiPost('/tenants', {
          slug: 'dev-tenant',
          displayName: manualCompany,
          identityBlock: manualCompany,
          postalAddress: '-',
          primaryContactEmail: 'onboarding@example.com',
        });
      } catch {
        // Already exists
      }

      try {
        await apiPatch('/tenants/current', {
          displayName: manualCompany || undefined,
          industry: manualIndustry || undefined,
          productName: manualProduct || undefined,
          targetAudience: manualAudience || undefined,
          senderFullName: manualName || undefined,
          senderRole: manualRole || 'CEO',
        });
      } catch {}

      updateManualItem(0, { status: 'done' });
      setProgress(20);

      if (abortRef.current) return;

      // ── Step 2: Generate leads (with SSE streaming) ──
      updateManualItem(1, { status: 'active' });

      const leadsResult = await streamAICallAsync<{ leads: any[]; count: number }>(
        `${API_URL}/ai/generate-leads`,
        {
          industry: manualIndustry || 'Technology',
          roleTitle: manualAudience || 'Decision Makers',
          region: manualRegion || 'Global',
          count: LEAD_COUNT,
          companyName: manualCompany,
          productName: manualProduct,
        },
        (msg) => updateManualItem(1, { detail: msg }),
      );

      updateManualItem(1, { status: 'done', detail: `${leadsResult.count} leads found` });
      setProgress(50);

      if (abortRef.current) return;

      // ── Step 3: Generate emails ──
      updateManualItem(2, { status: 'active' });

      const topicGoalText = `Introducing ${manualProduct || manualCompany} to ${manualAudience || 'potential customers'}`;

      await createCampaign({
        name: 'First Campaign',
        product: manualProduct || manualCompany,
        tone: 'professional',
        context: topicGoalText,
      });

      // Use the leads we just generated (they have IDs from the DB)
      const leads = leadsResult.leads;

      let emailCount = 0;
      let consecutiveErrors = 0;
      for (const lead of leads) {
        if (abortRef.current) return;
        try {
          updateManualItem(2, { detail: `Writing email ${emailCount + 1} of ${leads.length} — ${lead.firstName || ''} ${lead.lastName || ''} @ ${lead.company || ''}` });
          await apiPost<EmailGenerateResponse>('/emails/generate', {
            leadId: lead.id,
            topicGoalText,
          });
          emailCount++;
          consecutiveErrors = 0;
          setProgress(50 + Math.round((emailCount / leads.length) * 40));
        } catch (err: any) {
          consecutiveErrors++;
          if (consecutiveErrors >= 3) {
            updateManualItem(2, { detail: `${emailCount} of ${leads.length} written (limit reached)` });
            break;
          }
        }
      }

      updateManualItem(2, { status: emailCount > 0 ? 'done' : 'error', detail: emailCount > 0 ? `${emailCount} emails written` : 'Could not generate emails' });
      setProgress(95);

      // ── Step 4: Finalize ──
      updateManualItem(3, { status: 'active' });
      updateManualItem(3, { status: 'done' });
      setProgress(100);

      await new Promise((r) => setTimeout(r, 800));
      navigate('/onboarding/ready');
    } catch (err: any) {
      toast.error(err.message || 'Something went wrong. Please try again.');
      setPhase('manual');
      setProgress(0);
    }
  }

  async function handleSkip() {
    abortRef.current = true;
    try {
      const slug = `user-${Date.now()}`;
      await apiPost('/tenants', {
        slug,
        displayName: 'My Company',
        postalAddress: '-',
        primaryContactEmail: `skip-${Date.now()}@placeholder.local`,
      });
    } catch {
      // Tenant may already exist
    }
    await refreshUser();
    navigate('/dashboard');
  }

  return (
    <div className="min-h-screen bg-base-100 relative dot-grid">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-base-300/20 z-50">
        <div
          className="h-full bg-primary transition-all duration-700 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Skip link */}
      <div className="fixed top-4 right-4 z-50">
        <button
          onClick={handleSkip}
          className="text-xs text-base-content/30 hover:text-base-content/60 transition-colors"
        >
          Skip to app
        </button>
      </div>

      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-lg">
          {/* ── Phase 1: URL Input ── */}
          {phase === 'input' && (
            <div className="animate-fade-in space-y-8 text-center">
              <div>
                <h1 className="text-3xl font-bold tracking-tight mb-2">
                  What&apos;s your company website?
                </h1>
                <p className="text-base-content/40 text-sm">
                  Just enter your URL. We&apos;ll handle everything else.
                </p>
              </div>

              <div className="space-y-4">
                <input
                  type="url"
                  value={companyUrl}
                  onChange={(e) => setCompanyUrl(e.target.value)}
                  placeholder="yourwebsite.com"
                  className="input input-bordered w-full text-center text-lg"
                  onKeyDown={(e) => e.key === 'Enter' && handleGo()}
                  autoFocus
                />
                <button
                  onClick={handleGo}
                  disabled={!companyUrl.trim()}
                  className="btn btn-primary w-full gap-2"
                >
                  Go <ArrowRight size={16} />
                </button>
              </div>

              <button
                onClick={() => setPhase('manual')}
                className="text-xs text-base-content/30 hover:text-base-content/50 transition-colors"
              >
                I don&apos;t have a website
              </button>
            </div>
          )}

          {/* ── Manual Input (no website) ── */}
          {phase === 'manual' && (
            <div className="animate-fade-in space-y-6">
              <div className="text-center">
                <h1 className="text-2xl font-bold tracking-tight mb-2">
                  Tell us about your business
                </h1>
                <p className="text-base-content/40 text-sm">
                  We&apos;ll use this to find leads and write emails for you.
                </p>
              </div>

              <div className="bg-base-200 rounded-2xl p-6 space-y-3">
                <div>
                  <label className="text-xs text-base-content/40 mb-1 block">Company name *</label>
                  <input
                    type="text"
                    value={manualCompany}
                    onChange={(e) => setManualCompany(e.target.value)}
                    placeholder="Your company name"
                    className={`input input-bordered input-sm w-full ${!manualCompany.trim() && manualCompany !== '' ? 'input-error' : ''}`}
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="text-xs text-base-content/40 mb-1 block">Product or service</label>
                  <input
                    type="text"
                    value={manualProduct}
                    onChange={(e) => setManualProduct(e.target.value)}
                    placeholder="What do you sell?"
                    className="input input-bordered input-sm w-full"
                  />
                </div>
                <div>
                  <label className="text-xs text-base-content/40 mb-1 block">Industry</label>
                  <input
                    type="text"
                    value={manualIndustry}
                    onChange={(e) => setManualIndustry(e.target.value)}
                    placeholder="e.g. SaaS, Real Estate, Consulting"
                    className="input input-bordered input-sm w-full"
                  />
                </div>
                <div>
                  <label className="text-xs text-base-content/40 mb-1 block">Target audience</label>
                  <input
                    type="text"
                    value={manualAudience}
                    onChange={(e) => setManualAudience(e.target.value)}
                    placeholder="e.g. CTOs at mid-size tech companies"
                    className="input input-bordered input-sm w-full"
                  />
                </div>
                <div>
                  <label className="text-xs text-base-content/40 mb-1 block">Region</label>
                  <input
                    type="text"
                    value={manualRegion}
                    onChange={(e) => setManualRegion(e.target.value)}
                    placeholder="e.g. US, Europe, Global"
                    className="input input-bordered input-sm w-full"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-base-content/40 mb-1 block">Your name</label>
                    <input
                      type="text"
                      value={manualName}
                      onChange={(e) => setManualName(e.target.value)}
                      placeholder="Full name"
                      className="input input-bordered input-sm w-full"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-base-content/40 mb-1 block">Your role</label>
                    <input
                      type="text"
                      value={manualRole}
                      onChange={(e) => setManualRole(e.target.value)}
                      placeholder="e.g. CEO, Founder"
                      className="input input-bordered input-sm w-full"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setPhase('input')} className="btn btn-ghost gap-1">
                  <ArrowLeft size={14} /> Back
                </button>
                <button
                  onClick={handleGoManual}
                  disabled={!manualCompany.trim()}
                  className="btn btn-primary flex-1 gap-2"
                >
                  Go <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* ── Phase 2: Processing with Checklist ── */}
          {phase === 'processing' && (
            <div className="animate-fade-in space-y-8">
              <div className="text-center">
                <h1 className="text-2xl font-bold tracking-tight mb-2">
                  Setting everything up
                </h1>
                <p className="text-base-content/40 text-sm">
                  This takes about a minute. We&apos;re building your first campaign.
                </p>
              </div>

              {/* Orbit animation */}
              <div className="flex justify-center">
                <div className="relative w-20 h-20">
                  <div className="absolute inset-0 ai-orbit">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-primary/70" />
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-primary/30" />
                  </div>
                  <div className="absolute inset-3 ai-orbit-reverse">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-primary/50" />
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-primary/20" />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-primary/40 animate-ai-pulse" />
                  </div>
                </div>
              </div>

              {/* Checklist */}
              <div className="space-y-3 max-w-sm mx-auto">
                {checklist.map((item, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-3 transition-all duration-300 ${
                      item.status === 'pending' ? 'opacity-30' : 'opacity-100'
                    }`}
                  >
                    <div className="w-6 h-6 flex items-center justify-center shrink-0">
                      {item.status === 'done' ? (
                        <div className="w-5 h-5 rounded-full bg-success/20 flex items-center justify-center animate-fade-in">
                          <Check size={12} className="text-success" />
                        </div>
                      ) : item.status === 'error' ? (
                        <div className="w-5 h-5 rounded-full bg-warning/20 flex items-center justify-center animate-fade-in">
                          <span className="text-warning text-xs font-bold">!</span>
                        </div>
                      ) : item.status === 'active' ? (
                        <Loader2 size={16} className="animate-spin text-primary" />
                      ) : (
                        <Circle size={14} className="text-base-content/20" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${item.status === 'active' ? 'text-primary font-medium' : item.status === 'done' ? 'text-base-content/70' : item.status === 'error' ? 'text-warning/80' : 'text-base-content/40'}`}>
                        {item.label}
                      </p>
                      {item.detail && item.status === 'pending' && (
                        <p className="text-[11px] text-base-content/20">{item.detail}</p>
                      )}
                      {item.detail && item.status === 'active' && (
                        <p className="text-[11px] text-primary/50 animate-fade-in truncate">{item.detail}</p>
                      )}
                      {item.detail && item.status === 'done' && (
                        <p className="text-[11px] text-success/60">{item.detail}</p>
                      )}
                      {item.detail && item.status === 'error' && (
                        <p className="text-[11px] text-warning/60">{item.detail}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Timer */}
              <div className="flex justify-center">
                <span className="text-xs text-base-content/25 flex items-center gap-1.5 font-mono-stat">
                  <Clock size={11} />
                  {formatElapsed(elapsed)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
